import React, { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { ALL_ROLES, ROLE_LABELS, hasRole } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";
import { Pencil, X, Check, UserPlus, Trash2, Archive, Key } from "lucide-react";

const DEFAULT_NEW_USER = { email: "", name: "", phone: "", roles: ["customer"] };

export default function AdminUsers() {
  const { user: actor } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draftRoles, setDraftRoles] = useState([]);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // Iter65 — Manual add
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState(DEFAULT_NEW_USER);
  const [addingBusy, setAddingBusy] = useState(false);

  // Iter65 — Delete confirmation
  const [confirmDel, setConfirmDel] = useState(null); // user object

  // Iter65 — Bulk select + archive
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Iter68 — Issue temp password
  const [issuedPwd, setIssuedPwd] = useState(null); // { email, temp_password }

  // Iter74 — Set explicit password modal
  const [setPwdFor, setSetPwdFor] = useState(null); // user object
  const [setPwdValue, setSetPwdValue] = useState("");
  const [setPwdBusy, setSetPwdBusy] = useState(false);

  const canMutate = hasRole(actor, "master_admin");

  const reload = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const flash = (m) => { setOkMsg(m); setTimeout(() => setOkMsg(""), 3500); };

  const startEdit = (u) => { setEditing(u.id); setDraftRoles([...u.roles]); setErr(""); };
  const cancelEdit = () => { setEditing(null); setDraftRoles([]); };
  const toggleRole = (role) => setDraftRoles((rs) =>
    rs.includes(role) ? rs.filter((r) => r !== role) : [...rs, role]
  );
  const saveRoles = async (id) => {
    try { await api.put(`/admin/users/${id}/roles`, { roles: draftRoles }); cancelEdit(); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  const updateStatus = async (id, status) => {
    try { await api.put(`/admin/users/${id}/status`, { status }); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };

  // ---- Manual add ----
  const toggleNewRole = (r) => setNewUser((u) =>
    ({ ...u, roles: u.roles.includes(r) ? u.roles.filter(x => x !== r) : [...u.roles, r] })
  );
  const submitNewUser = async () => {
    setErr("");
    if (!newUser.email || !newUser.name) { setErr("Email and name are required."); return; }
    if (newUser.roles.length === 0) { setErr("Pick at least one role."); return; }
    setAddingBusy(true);
    try {
      const { data } = await api.post("/admin/users", newUser);
      setShowAdd(false);
      setNewUser(DEFAULT_NEW_USER);
      flash(data?.temp_password
        ? `User created. Temporary password: ${data.temp_password} (share securely; user can rotate via Account → Security).`
        : "User created.");
      await reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setAddingBusy(false); }
  };

  // ---- Delete (soft archive) ----
  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/users/${confirmDel.id}`);
      flash(`Archived ${confirmDel.email}.`);
      setConfirmDel(null);
      await reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  // ---- Bulk archive ----
  // Iter68 — issue temp password
  const issueTempPassword = async (u) => {
    setErr("");
    try {
      const { data } = await api.post(`/admin/users/${u.id}/issue-temp-password`);
      setIssuedPwd({ email: data.email, temp_password: data.temp_password });
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  // Iter74 — set explicit password (Iter75 — add reveal of new password + clearer success)
  const submitSetPwd = async () => {
    if (!setPwdFor) return;
    if (!setPwdValue || setPwdValue.length < 8) { setErr("Password must be at least 8 chars."); return; }
    setSetPwdBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/users/${setPwdFor.id}/set-password`, { password: setPwdValue });
      if (!data?.ok) {
        setErr("Server did not confirm the change. Try again.");
        return;
      }
      // Iter75 — show the issued-pwd reveal modal so the admin can SEE the new
      // password and copy it. Auto-clears the set-password modal.
      setIssuedPwd({ email: setPwdFor.email, temp_password: setPwdValue });
      flash(`✅ Password updated for ${setPwdFor.email}. They can sign in immediately.`);
      setSetPwdFor(null); setSetPwdValue("");
    } catch (e) {
      setErr("Set-password failed: " + apiErrorMessage(e));
    }
    finally { setSetPwdBusy(false); }
  };  const eligibleForBulk = useMemo(
    () => users.filter(u => !(u.roles || []).includes("master_admin")),
    [users]
  );
  const toggleSel = (id) => setSelectedIds((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelectedIds((s) =>
    s.size === eligibleForBulk.length ? new Set() : new Set(eligibleForBulk.map(u => u.id))
  );
  const doBulkArchive = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk",
        { user_ids: Array.from(selectedIds), action: "archive" });
      flash(`Archived ${data.modified} user${data.modified === 1 ? "" : "s"}. ` +
            (data.skipped_master_admins?.length ? `(${data.skipped_master_admins.length} master_admin protected)` : ""));
      setSelectedIds(new Set());
      setBulkConfirm(false);
      await reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-6">
      <header className="fade-up flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">Users</div>
          <h1 className="headline-xl">User management</h1>
          <p className="text-lifted-dim mt-2 text-sm max-w-2xl">
            {canMutate
              ? "Master Admin: add, edit roles, archive, or bulk-archive users. Master admin accounts are always protected."
              : "Admins can view users and toggle status. Only master admin may modify roles, add, or archive."}
          </p>
        </div>
        {canMutate && (
          <div className="flex gap-2">
            <button
              data-testid={ADMIN.addUserBtn}
              onClick={() => { setShowAdd(true); setErr(""); }}
              className="btn-lifted !py-2 !px-3 !text-xs inline-flex items-center gap-1.5"
            >
              <UserPlus className="h-3.5 w-3.5" /> Add user
            </button>
            <button
              data-testid={ADMIN.bulkArchiveBtn}
              onClick={() => setBulkConfirm(true)}
              disabled={selectedIds.size === 0}
              className="btn-ghost !py-2 !px-3 !text-xs inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Archive className="h-3.5 w-3.5" /> Bulk archive ({selectedIds.size})
            </button>
          </div>
        )}
      </header>

      {err && <div data-testid="admin-users-error" className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">{err}</div>}
      {okMsg && <div data-testid="admin-users-ok" className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2">{okMsg}</div>}

      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid={ADMIN.usersTable} className="w-full text-sm">
            <thead>
              <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
                {canMutate && (
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      data-testid={ADMIN.bulkCheckAll}
                      checked={selectedIds.size > 0 && selectedIds.size === eligibleForBulk.length}
                      onChange={toggleAll}
                    />
                  </th>
                )}
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Roles</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMaster = (u.roles || []).includes("master_admin");
                return (
                  <tr key={u.id} data-testid={ADMIN.userRow(u.id)} className="border-b border-white/5 hover:bg-white/[0.02]">
                    {canMutate && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          data-testid={ADMIN.bulkCheckbox(u.id)}
                          checked={selectedIds.has(u.id)}
                          onChange={() => toggleSel(u.id)}
                          disabled={isMaster}
                          title={isMaster ? "Master admin protected" : ""}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="font-medium text-lifted-text">{u.name}</div>
                      <div className="text-xs text-lifted-dim">{u.email}</div>
                    </td>

                    <td className="px-4 py-3">
                      {editing === u.id ? (
                        <div className="flex flex-wrap gap-1.5 max-w-md">
                          {ALL_ROLES.map((r) => {
                            const on = draftRoles.includes(r);
                            return (
                              <button
                                key={r}
                                type="button"
                                data-testid={ADMIN.roleCheckbox(u.id, r)}
                                onClick={() => toggleRole(r)}
                                className={`chip cursor-pointer ${on ? "chip--green" : ""}`}
                              >
                                {ROLE_LABELS[r]}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {(u.roles || []).map((r) => (
                            <span key={r} className={`chip ${r === u.primary_role ? "chip--green" : "chip--purple"}`}>
                              {ROLE_LABELS[r] || r}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <select
                        data-testid={ADMIN.statusSelect(u.id)}
                        value={u.status}
                        onChange={(e) => updateStatus(u.id, e.target.value)}
                        className="bg-lifted-surface border border-white/10 rounded-md px-2 py-1 text-xs font-bebas tracking-[0.18em] uppercase"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                        <option value="archived">Archived</option>
                      </select>
                    </td>

                    <td className="px-4 py-3 text-right">
                      {editing === u.id ? (
                        <div className="inline-flex gap-2">
                          <button data-testid={ADMIN.saveRolesBtn(u.id)} onClick={() => saveRoles(u.id)} className="btn-lifted !py-1.5 !px-3 !text-[0.7rem]">
                            <Check className="h-3 w-3" /> Save
                          </button>
                          <button data-testid={ADMIN.cancelRolesBtn(u.id)} onClick={cancelEdit} className="btn-ghost !py-1.5 !px-3 !text-[0.7rem]">
                            <X className="h-3 w-3" /> Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex gap-2">
                          <button
                            data-testid={ADMIN.editRolesBtn(u.id)}
                            onClick={() => startEdit(u)}
                            disabled={!canMutate}
                            className="btn-ghost !py-1.5 !px-3 !text-[0.7rem] disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Pencil className="h-3 w-3" /> Edit
                          </button>
                          {canMutate && !isMaster && (
                            <button
                              data-testid={`admin-issue-pwd-${u.id}`}
                              onClick={() => issueTempPassword(u)}
                              className="btn-ghost !py-1.5 !px-3 !text-[0.7rem]"
                              title="Issue random temporary password"
                            >
                              <Key className="h-3 w-3" /> Pwd
                            </button>
                          )}
                          {canMutate && !isMaster && (
                            <button
                              data-testid={`admin-set-pwd-${u.id}`}
                              onClick={() => { setSetPwdFor(u); setSetPwdValue(""); setErr(""); }}
                              className="btn-ghost !py-1.5 !px-3 !text-[0.7rem]"
                              title="Set explicit password (master admin only)"
                            >
                              <Key className="h-3 w-3" /> Set
                            </button>
                          )}
                          {canMutate && !isMaster && (
                            <button
                              data-testid={ADMIN.deleteUserBtn(u.id)}
                              onClick={() => setConfirmDel(u)}
                              className="btn-ghost !py-1.5 !px-3 !text-[0.7rem] text-rose-300 hover:text-rose-200"
                              title="Archive user (soft delete)"
                            >
                              <Trash2 className="h-3 w-3" /> Archive
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={canMutate ? 5 : 4} className="px-4 py-10 text-center text-lifted-dim">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ----- Add user modal ----- */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div data-testid={ADMIN.addUserModal} className="glass-panel max-w-md w-full p-5 border border-white/10">
            <h2 className="font-anton text-xl uppercase text-white mb-3">Add user</h2>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-bebas text-lifted-dim mb-1">Email *</label>
                <input
                  data-testid={ADMIN.addUserEmail}
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full bg-lifted-surface border border-white/10 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-bebas text-lifted-dim mb-1">Name *</label>
                <input
                  data-testid={ADMIN.addUserName}
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full bg-lifted-surface border border-white/10 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-bebas text-lifted-dim mb-1">Phone (optional)</label>
                <input
                  type="text"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="w-full bg-lifted-surface border border-white/10 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-bebas text-lifted-dim mb-1">Roles</label>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_ROLES.map((r) => {
                    const on = newUser.roles.includes(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        data-testid={ADMIN.addUserRoleCb(r)}
                        onClick={() => toggleNewRole(r)}
                        className={`chip cursor-pointer ${on ? "chip--green" : ""}`}
                      >
                        {ROLE_LABELS[r] || r}
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-[11px] text-lifted-dim">
                A temporary password is auto-generated. Share it securely; the user can rotate it under Account → Security.
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                data-testid={ADMIN.addUserCancel}
                onClick={() => { setShowAdd(false); setNewUser(DEFAULT_NEW_USER); }}
                className="btn-ghost !py-2 !px-3 !text-xs"
              >Cancel</button>
              <button
                data-testid={ADMIN.addUserSubmit}
                onClick={submitNewUser}
                disabled={addingBusy}
                className="btn-lifted !py-2 !px-3 !text-xs"
              >{addingBusy ? "Adding…" : "Create user"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ----- Delete confirm ----- */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel max-w-md w-full p-5 border border-rose-400/30">
            <h2 className="font-anton text-xl uppercase text-white mb-1">Archive user?</h2>
            <p className="text-sm text-lifted-dim mb-4">
              This soft-archives <strong>{confirmDel.email}</strong>. They will no longer appear in the default user list.
              No data is hard-deleted; the audit trail is preserved.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="btn-ghost !py-2 !px-3 !text-xs">Cancel</button>
              <button
                data-testid={ADMIN.confirmDelete}
                onClick={doDelete}
                className="btn-lifted !py-2 !px-3 !text-xs bg-rose-500/80 hover:bg-rose-500"
              >Archive user</button>
            </div>
          </div>
        </div>
      )}

      {/* ----- Bulk archive confirm ----- */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel max-w-md w-full p-5 border border-amber-400/30">
            <h2 className="font-anton text-xl uppercase text-white mb-1">Bulk archive {selectedIds.size} users?</h2>
            <p className="text-sm text-lifted-dim mb-4">
              All selected users will be soft-archived (status = archived).
              Master Admin accounts are automatically excluded by the server.
              This is reversible — flip their status back to active to restore.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setBulkConfirm(false)} className="btn-ghost !py-2 !px-3 !text-xs">Cancel</button>
              <button
                data-testid={ADMIN.bulkConfirm}
                onClick={doBulkArchive}
                disabled={bulkBusy}
                className="btn-lifted !py-2 !px-3 !text-xs"
              >{bulkBusy ? "Archiving…" : "Yes, archive"}</button>
            </div>
          </div>
        </div>
      )}
      {/* ----- Iter74 — Set explicit password modal ----- */}
      {setPwdFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel max-w-md w-full p-5 border border-lifted-green/40">
            <h2 className="font-anton text-xl uppercase text-white mb-2">Set password</h2>
            <p className="text-sm text-lifted-dim mb-3">
              For <strong className="text-white">{setPwdFor.email}</strong>. The user can change it later under Account → Security.
            </p>
            <input
              data-testid="admin-set-pwd-input"
              type="text"
              value={setPwdValue}
              onChange={(e) => setSetPwdValue(e.target.value)}
              placeholder="Min 8 characters"
              className="w-full bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm font-mono"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setSetPwdFor(null); setSetPwdValue(""); }} className="btn-ghost !py-2 !px-3 !text-xs">Cancel</button>
              <button
                data-testid="admin-set-pwd-submit"
                onClick={submitSetPwd}
                disabled={setPwdBusy || !setPwdValue}
                className="btn-lifted !py-2 !px-3 !text-xs"
              >{setPwdBusy ? "Setting…" : "Set password"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ----- Iter68 — Issued temp password reveal modal ----- */}
      {issuedPwd && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel max-w-md w-full p-5 border border-lifted-green/40">
            <h2 className="font-anton text-xl uppercase text-white mb-2">Temporary password issued</h2>
            <p className="text-sm text-lifted-dim mb-3">
              Share this securely with <strong className="text-white">{issuedPwd.email}</strong>.
              They must rotate it under Account → Security on first login.
              You can re-issue at any time if it's lost.
            </p>
            <div className="bg-black/60 border border-lifted-green/30 rounded-md p-3 mb-3 font-mono text-base text-lifted-green break-all select-all">
              {issuedPwd.temp_password}
            </div>
            <div className="flex justify-end gap-2">
              <button
                data-testid="admin-issued-pwd-copy"
                onClick={() => navigator.clipboard?.writeText(issuedPwd.temp_password)}
                className="btn-ghost !py-2 !px-3 !text-xs"
              >Copy</button>
              <button
                onClick={() => setIssuedPwd(null)}
                className="btn-lifted !py-2 !px-3 !text-xs"
              >Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
